export interface CornerRadii {
    topLeftRadius: number;
    topRightRadius: number;
    bottomRightRadius: number;
    bottomLeftRadius: number;
}

const MIN_VISIBLE_RADIUS = 1;

const CORNER_SEGMENTS = 16;

const scaleRadiusPair = (radii: [number, number], max: number): [number, number] => {
    const total = radii[0] + radii[1];
    if (total <= max || total === 0) {
        return radii;
    }
    return [(radii[0] / total) * max, (radii[1] / total) * max];
};

/**
 * Scales corner radii down, preserving their relative proportions, so that no pair of adjacent
 * radii overlaps along either the width or the height of the rectangle they bound.
 */
export const clampCornerRadii = (
    radii: CornerRadii,
    width: number,
    height: number,
): CornerRadii => {
    let { topLeftRadius, topRightRadius, bottomRightRadius, bottomLeftRadius } = radii;

    [topLeftRadius, topRightRadius] = scaleRadiusPair([topLeftRadius, topRightRadius], width);
    [bottomLeftRadius, bottomRightRadius] = scaleRadiusPair(
        [bottomLeftRadius, bottomRightRadius],
        width,
    );
    [topLeftRadius, bottomLeftRadius] = scaleRadiusPair([topLeftRadius, bottomLeftRadius], height);
    [topRightRadius, bottomRightRadius] = scaleRadiusPair(
        [topRightRadius, bottomRightRadius],
        height,
    );

    return { topLeftRadius, topRightRadius, bottomRightRadius, bottomLeftRadius };
};

const tessellateCornerArc = (
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
): [number, number][] => {
    const points: [number, number][] = [];
    for (let i = 0; i <= CORNER_SEGMENTS; i++) {
        const angle = startAngle + (i / CORNER_SEGMENTS) * (Math.PI / 2);
        points.push([centerX + radius * Math.cos(angle), centerY + radius * Math.sin(angle)]);
    }
    return points;
};

/**
 * Returns a clockwise list of points tracing the perimeter of a rectangle with independently
 * rounded corners, starting from the top-left.
 */
export const getRoundedRectPerimeterPoints = (
    x: number,
    y: number,
    width: number,
    height: number,
    radii: CornerRadii,
): [number, number][] => {
    const { topLeftRadius, topRightRadius, bottomRightRadius, bottomLeftRadius } = clampCornerRadii(
        radii,
        width,
        height,
    );

    const corners: {
        radius: number;
        centerX: number;
        centerY: number;
        startAngle: number;
        sharpPoint: [number, number];
    }[] = [
        {
            radius: topLeftRadius,
            centerX: x + topLeftRadius,
            centerY: y + topLeftRadius,
            startAngle: Math.PI,
            sharpPoint: [x, y],
        },
        {
            radius: topRightRadius,
            centerX: x + width - topRightRadius,
            centerY: y + topRightRadius,
            startAngle: -Math.PI / 2,
            sharpPoint: [x + width, y],
        },
        {
            radius: bottomRightRadius,
            centerX: x + width - bottomRightRadius,
            centerY: y + height - bottomRightRadius,
            startAngle: 0,
            sharpPoint: [x + width, y + height],
        },
        {
            radius: bottomLeftRadius,
            centerX: x + bottomLeftRadius,
            centerY: y + height - bottomLeftRadius,
            startAngle: Math.PI / 2,
            sharpPoint: [x, y + height],
        },
    ];

    const points: [number, number][] = [];
    corners.forEach(({ radius, centerX, centerY, startAngle, sharpPoint }) => {
        if (radius < MIN_VISIBLE_RADIUS) {
            points.push(sharpPoint);
        } else {
            points.push(...tessellateCornerArc(centerX, centerY, radius, startAngle));
        }
    });
    return points;
};
